import React from 'react';
import { Select, SelectItem } from '@nextui-org/react';
import { Species } from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';

interface UnitPriceFilterProps {
  selectedSpecies: Species | null;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  label?: string;
}

/**
 * UnitPriceFilter component for switching between kg and box price view
 *
 * @param selectedSpecies - The currently selected species which determines the box size
 * @param value - Current selected value (format: 'kg' or 'box-{size}')
 * @param onChange - Callback when selection changes
 * @param className - Additional CSS classes
 * @param label - Custom label for the select component
 */
const UnitPriceFilter: React.FC<UnitPriceFilterProps> = ({
  selectedSpecies,
  value,
  onChange,
  className = '',
  label,
}) => {
  const { t } = useTranslation('market');

  // Determine box sizes based on the selected species or use default 5kg when species is null
  const boxSizes = React.useMemo(() => {
    if (!selectedSpecies) {
      return [5]; // Default to 5kg box for settlements
    }

    if (
      !selectedSpecies?.default_unit_price ||
      selectedSpecies.default_unit_price <= 1
    ) {
      return [];
    }
    return [selectedSpecies.default_unit_price];
  }, [selectedSpecies]);

  // Use translated label or fallback to prop
  const finalLabel = label || t('Unidad de precio');

  return (
    <Select
      label={finalLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      selectedKeys={[value]}
      className={className}
    >
      <SelectItem key='kg' value='kg' textValue={t('Precio por kg')}>
        {label === 'Kg o Caja Eq.' ? 'Kg' : t('Precio por kg')}
      </SelectItem>
      {
        boxSizes.map((size) => (
          <SelectItem
            key={`box-${size}`}
            value={`box-${size}`}
            textValue={`${t('Precio por caja')} - ${size}${t('kgs')}`}
          >
            {label === 'Kg o Caja Eq.'
              ? 'Caja Eq.'
              : `${t('Precio por caja')} - ${size}${t('kgs')}`}
          </SelectItem>
        )) as any
      }
    </Select>
  );
};

export default UnitPriceFilter;
